import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { trackAdformOnStart } from 'api/analytics-api';
import { logger } from 'api/logging-api';
import styles from 'App.module.scss';
import { type CoveragePremium } from 'dto/CoveragePremium';
import { ProfilingResult } from 'dto/ProfilingResult';
import { setErrorState } from 'individual/components/common/ErrorBoundary';
import Footer from 'individual/components/Footer';
import { LoadingScreen } from 'individual/views/LoadingScreen';
import { useAppStore } from 'state/store';
import { toEditableForm } from 'state/util';
import { getInitialState, getPath } from 'utils';

const Layout = () => {
  const { ageInvalid, calculatorInput, profilingResult } = getInitialState();
  const setNumberOfInstallments = useAppStore(
    (state) => state.setNumberOfInstallments
  );
  const setInitialRatingLoading = useAppStore(
    (state) => state.setInitialRatingLoading
  );
  const submitBackgroundInfo = useAppStore(
    (state) => state.submitBackgroundInfo
  );
  const { initialRatingLoading, languageConfirmed, blockingUWIssues } =
    useAppStore(
      useShallow((state) => ({
        initialRatingLoading: state.initialRatingLoading,
        languageConfirmed: state.languageConfirmed,
        blockingUWIssues: state.blockingUWIssues,
      }))
    );
  const backgroundInfoForm = useAppStore.use.backgroundInfoForm();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
    window.scrollTo(0, 0);
  }, [location]);

  // Submit initial rating if calculatorInput exists (user returning from calculator)
  useEffect(() => {
    if (calculatorInput && backgroundInfoForm.dateOfBirth) {
      setInitialRatingLoading(true);
      submitBackgroundInfo(toEditableForm(backgroundInfoForm))
        .then((value: CoveragePremium[]) => {
          if (calculatorInput) {
            setNumberOfInstallments(calculatorInput.installmentAmount);
          }
          if (locationRef.current.pathname === '/') {
            navigate(getPath(t('paths.insuranceContent')), { replace: true });
          }
          setInitialRatingLoading(false);
          return value;
        })
        .catch((e: Error) => {
          setInitialRatingLoading(false);
          logger.logError(e);
          setErrorState(true);
        });
    }
  }, []);

  useEffect(() => {
    if (ageInvalid) {
      navigate(getPath(t('paths.ageLimit')), { replace: true });
    } else if (
      profilingResult === ProfilingResult.REQUIRE_LANGUAGE_CONFIRMATION &&
      !languageConfirmed
    ) {
      navigate(getPath(t('paths.language')), { replace: true });
    } else if (profilingResult === ProfilingResult.BLOCK) {
      navigate(getPath(t('paths.block')), { replace: true });
    } else if (blockingUWIssues) {
      navigate(getPath(t('paths.result')), { replace: true });
    }
  }, [ageInvalid, profilingResult, languageConfirmed, blockingUWIssues]);

  useEffect(() => {
    if (!ageInvalid && profilingResult !== ProfilingResult.BLOCK) {
      trackAdformOnStart();
    }
  }, []);

  return (
    <div
      className={`${styles.root as string} opds opux-v6__opds opds-styles-v1-elements-headings-small opds-styles-v1-text-body-large`}>
      {initialRatingLoading ? (
        <LoadingScreen />
      ) : (
        <>
          <Outlet />
          <Footer />
        </>
      )}
    </div>
  );
};

export default Layout;
